const STORAGE_KEY = "mf_last_page";

// How long a persisted "last visited numbered LP" stays valid — bounds the
// fallback App.js/SuccessPage.js/FailedPage.js use for a genuine carrier
// callback round-trip. Kept tight (1 minute) so a browser's last-touched LP
// can't leak into an unrelated later visit — note this is close to the ~15s
// worst-case one-click HE confirm we've observed, so a visitor who sits on
// the carrier's confirmation step for longer than that may fall through to
// the generic/default page instead of their original numbered LP. Keep in
// sync with public/index.html's saveLastPageId, which writes the `savedAt`
// timestamp this reads.
const MAX_AGE_MS = 2 * 60 * 1000;

// Mirrors public/index.html's CAMP_TO_PIN_PATH page ids. Used to validate a
// stored value before redirecting to it.
export const KNOWN_PAGE_IDS = [
  "7812", "2231", "4237", "8609", "6364", "1802", "3813",
];

// Maps the campaign "code" returned by the campaign-lookup endpoint to that
// page's own numeric id. Single source of truth — src/App.js and the
// default-flow success/failed dispatch pages both import this instead of
// keeping their own copy.
export const CAMP_TO_PAGE_ID = {
  2001: "7812",
  2002: "2231",
  2003: "4237",
  2004: "8609",
  2005: "6364",
  2006: "1802",
  2007: "3813",
};

/**
 * True when the current page was reached via a full-page navigation from a
 * different origin (e.g. the operator's carrier gateway redirecting the
 * browser back to us) rather than a manually-typed URL, a bookmark, or a
 * same-origin navigation. Manual/direct visits report an empty
 * document.referrer in effectively every browser, so this is a reliable way
 * to distinguish "the operator sent me here" from "I just typed this URL" —
 * both of which can otherwise look identical (no query params at all).
 */
export function cameFromExternalRedirect() {
  if (!document.referrer) return false;

  try {
    return new URL(document.referrer).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Persists which numbered campaign page the visitor is on, so it survives an
 * operator redirect round-trip that strips our original query params (e.g.
 * the fixed /lp/success and /lp/failed routes the Zain carrier always sends
 * users back to). Written by
 * public/index.html's bootstrap script on every page visit; read here.
 */
export function saveLastPageId(pageId) {
  try {
    if (pageId) window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ pageId, savedAt: Date.now() }));
  } catch {
    // localStorage unavailable (private mode, disabled) — non-fatal.
  }
}

/**
 * Returns the persisted page id, or null if it's missing, malformed, or
 * older than MAX_AGE_MS — clearing it from storage in the latter two cases
 * so a stale/corrupt value doesn't keep getting re-read on every visit.
 */
export function getLastPageId() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const pageId = parsed?.pageId;
    const savedAt = parsed?.savedAt;

    if (!pageId || typeof savedAt !== "number" || Date.now() - savedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return pageId;
  } catch {
    return null;
  }
}
